import React, { useMemo, useState } from 'react'
import { styled } from 'linaria/react'
import { cx } from 'linaria'
import { Search } from 'iconoir-react'

const OptionsWrapper = styled.div`
   .ms-search {
      position: relative;
      padding: 0.75rem;
      border-bottom: 1px solid var(--color-separator);
   }

   .ms-search-icon {
      position: absolute;
      left: 1.6rem;
      top: 50%;
      transform: translateY(-50%);
      color: #707070;
      pointer-events: none;
   }

   .ms-search-input {
      width: 100%;
      padding: 10px 12px 10px 40px;
      border: 2px solid var(--color-vb-blue);
      border-radius: 999px;
      font-family: var(--font-industry);
      font-weight: 500;
      font-size: 1rem;

      &:focus {
         outline: none;
         border-color: var(--color-vb-blue-darker);
      }
   }

   .ms-list {
      list-style: none;
      margin: 0;
      padding: 0.25rem 0;
      max-height: 300px;
      overflow-y: auto;
   }

   .ms-option {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.55rem 1rem;
      cursor: pointer;
      font-weight: 600;

      &:hover {
         background-color: var(--color-vb-gray-2);
      }
   }

   .ms-option .form-check-input {
      flex-shrink: 0;
      margin: 0;
      width: 18px;
      height: 18px;
      cursor: pointer;
      border-color: #707070;
      box-shadow: none;
      border-radius: 5px;

      &:checked {
         background-color: var(--color-vb-blue);
         border-color: var(--color-vb-blue-darker);
      }
   }

   .ms-option-text {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      min-width: 0;
   }

   .ms-option-label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
   }

   .ms-option-dot {
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      border-radius: 50%;
   }

   .ms-option-subtitle {
      font-size: 0.85rem;
      font-weight: 400;
      color: #707070;
   }

   .ms-option-count {
      color: var(--color-black);
      font-size: 0.85rem;
      font-weight: 500;
   }

   .ms-empty {
      padding: 0.75rem 1rem;
      color: #707070;
   }

   .ms-show-all {
      background: none;
      border: none;
      padding: 0.5rem 0 0;
      font-weight: 700;
      text-decoration: underline;
      color: var(--color-vb-blue);
      cursor: pointer;
   }

   /* Variant used inside the mobile filter drawer: no inner scroll, larger touch targets */
   &.is-inline {
      .ms-search {
         padding: 0 0 0.5rem;
         border-bottom: none;
      }
      .ms-search-icon {
         left: 0.9rem;
         top: calc(50% - 0.25rem);
      }
      .ms-list {
         max-height: none;
         overflow: visible;
      }
      .ms-option {
         padding: 0.65rem 0;
         &:hover {
            background-color: transparent;
         }
      }
      .ms-option .form-check-input {
         width: 22px;
         height: 22px;
      }
      .ms-empty {
         padding: 0.75rem 0;
      }
   }
`

export const getSelectedValues = (filters, fieldName) => {
   const value = filters?.find(f => f?.fieldName === fieldName)?.value
   if (!value) return []
   return Array.isArray(value) ? value : [value]
}

const getOptionLabel = option => (option.label ?? option.value).toString()

const SORTERS = {
   label: (a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b), 'sv'),
   count: (a, b) =>
      (b.count ?? 0) - (a.count ?? 0) ||
      getOptionLabel(a).localeCompare(getOptionLabel(b), 'sv'),
}

/**
 * Searchable checkbox list for multi-select facets, e.g. brands, product types or stock status.
 * Used inside the desktop dropdowns and the mobile filter drawer.
 *
 * @param {string} fieldName - Filter field name the selected values are stored under.
 * @param {Array<{value: string, count: number, label?: string, subtitle?: string, color?: string}>} options
 *    Facet values to choose from. `label` replaces the raw value in the UI, `subtitle` is shown below it
 *    and `color` renders a coloured dot before the label.
 * @param {string} searchPlaceholder - Placeholder of the search field.
 * @param {string} emptyText - Shown when the search matches no options.
 * @param {boolean} searchable - Show the search field.
 * @param {'label'|'count'|'none'} sortBy - Option order, 'none' keeps the given order.
 * @param {boolean} selectedFirst - Show selected options at the top.
 * @param {number} visibleLimit - Only show this many options until "show all" is clicked.
 * @param {function} getShowAllLabel - Label for the "show all" button, called with the total option count.
 * @param {boolean} inline - Drawer variant without inner scroll.
 */
const MultiSelectFilterOptions = ({
   fieldName,
   filters,
   options,
   updateFilters,
   searchPlaceholder,
   emptyText,
   searchable = true,
   sortBy = 'label',
   selectedFirst = false,
   visibleLimit,
   getShowAllLabel,
   inline = false,
   autoFocus = false,
}) => {
   const [searchValue, setSearchValue] = useState('')
   const [showAll, setShowAll] = useState(false)
   const selectedValues = getSelectedValues(filters, fieldName)
   const selectedKey = selectedValues.join('|')

   const matchingOptions = useMemo(() => {
      const query = searchValue.trim().toLowerCase()
      const matches = (options ?? [])
         .filter(option => option?.value)
         .filter(
            option =>
               !query || getOptionLabel(option).toLowerCase().includes(query)
         )
      const sorted = SORTERS[sortBy]
         ? [...matches].sort(SORTERS[sortBy])
         : matches
      if (!selectedFirst) return sorted
      const selected = selectedKey.split('|')
      return [
         ...sorted.filter(option => selected.includes(option.value)),
         ...sorted.filter(option => !selected.includes(option.value)),
      ]
   }, [options, searchValue, sortBy, selectedFirst, selectedKey])

   // The limit only applies while browsing, a search always shows every match
   const isLimited =
      !!visibleLimit &&
      !showAll &&
      !searchValue &&
      matchingOptions.length > visibleLimit
   const visibleOptions = isLimited
      ? matchingOptions.slice(0, visibleLimit)
      : matchingOptions

   const toggleValue = value => {
      updateFilters?.([{ fieldName, value }])
   }

   return (
      <OptionsWrapper className={cx(inline && 'is-inline')}>
         {searchable && (
            <div className="ms-search">
               <Search className="ms-search-icon" width={18} height={18} />
               <input
                  type="search"
                  className="ms-search-input"
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  autoFocus={autoFocus}
               />
            </div>
         )}
         <ul className="ms-list">
            {visibleOptions.map(option => {
               const inputId = `${fieldName}-${
                  inline ? 'inline' : 'dropdown'
               }-${option.value}`
               return (
                  <li key={option.value}>
                     <label className="ms-option" htmlFor={inputId}>
                        <input
                           id={inputId}
                           type="checkbox"
                           className="form-check-input"
                           checked={selectedValues.includes(option.value)}
                           onChange={() => toggleValue(option.value)}
                        />
                        <span className="ms-option-text">
                           <span className="ms-option-label">
                              {option.color && (
                                 <span
                                    className="ms-option-dot"
                                    style={{ backgroundColor: option.color }}
                                 />
                              )}
                              {getOptionLabel(option)}
                           </span>
                           {option.subtitle && (
                              <span className="ms-option-subtitle">
                                 {option.subtitle}
                              </span>
                           )}
                        </span>
                        {option.count > 0 && (
                           <span className="ms-option-count">
                              {option.count}
                           </span>
                        )}
                     </label>
                  </li>
               )
            })}
            {visibleOptions.length === 0 && (
               <li className="ms-empty">{emptyText}</li>
            )}
         </ul>
         {isLimited && (
            <button
               type="button"
               className="ms-show-all"
               onClick={() => setShowAll(true)}
            >
               {getShowAllLabel?.(matchingOptions.length)}
            </button>
         )}
      </OptionsWrapper>
   )
}

export default MultiSelectFilterOptions
